import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ShareButtons from "../components/ShareButtons";
import VerifiedBadge from "../components/VerifiedBadge";
import StarRating from "../components/StarRating";
import JobCard from "../components/JobCard";
import LocationAutocomplete from "../components/LocationAutocomplete";
import { usePolling } from "../hooks/usePolling";
import { toast } from "sonner";
import { MapPin, Calendar, IndianRupee, Users, Clock, Settings } from "lucide-react";

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [applied, setApplied] = useState(false);
  const [appStatus, setAppStatus] = useState(null);
  const [myApp, setMyApp] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [ratingStars, setRatingStars] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [alreadyRated, setAlreadyRated] = useState(false);
  const [submittingRating, setSubmittingRating] = useState(false);
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);
  const [applyHomeLocation, setApplyHomeLocation] = useState("");
  const [applyHomeLat, setApplyHomeLat] = useState(null);
  const [applyHomeLng, setApplyHomeLng] = useState(null);

  const isOwner = user && job && user.id === job.posted_by;

  // Polls only the job stats (slots, confirmed, waitlist) — lightweight
  const loadJob = useCallback(async () => {
    try {
      const { data } = await api.get(`/jobs/${id}`);
      setJob(data);
      if (data.posted_by) {
        try {
          const { data: r } = await api.get(`/ratings/${data.posted_by}`);
          setRatings(r);
        } catch {}
      }
    } catch {}
  }, [id]);

  // User-specific state — only on mount + after actions
  const loadUserState = useCallback(async (postedBy) => {
    if (!user) return;
    try {
      const { data: apps } = await api.get("/me/applications");
      const mine = apps.find(a => a.job_id === id);
      if (mine) {
        setApplied(true); setAppStatus(mine.status); setMyApp(mine);
      } else {
        setApplied(false); setAppStatus(null); setMyApp(null);
      }
    } catch {}
    if (postedBy) {
      try {
        const { data: rc } = await api.get(`/ratings/check/${id}/${postedBy}`);
        setAlreadyRated(rc.rated);
      } catch {}
    }
  }, [id, user]);

  // Full initial load
  useEffect(() => {
    const init = async () => {
      try {
        const { data } = await api.get(`/jobs/${id}`);
        setJob(data);
        if (data.posted_by) {
          try { const { data: r } = await api.get(`/ratings/${data.posted_by}`); setRatings(r); } catch {}
        }
        await loadUserState(data.posted_by);
      } catch { toast.error("Failed to load job"); }
    };
    init();
  }, [id, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll job stats every 10s, pauses when tab is hidden
  usePolling(loadJob, 10000, !!job);

  const apply = async () => {
    if (!user) {
      try { localStorage.setItem("em_pending_apply", JSON.stringify({ jobId: id, ts: Date.now() })); } catch {}
      return nav(`/login?redirect=${encodeURIComponent(`/job/${id}`)}`);
    }
    setShowApplyConfirm(true);
  };

  const confirmApply = async () => {
    setShowApplyConfirm(false);
    if (applyHomeLat != null && applyHomeLng != null) {
      // Optional — enables travel expense reimbursement. Non-blocking on failure.
      try {
        await api.patch("/users/me", { location: applyHomeLocation, lat: applyHomeLat, lng: applyHomeLng });
      } catch {
        // Worker can set this later from their profile instead.
      }
    }
    try {
      const { data } = await api.post(`/jobs/${id}/apply`);
      setApplied(true); setAppStatus(data.status);
      toast.success(data.status === "confirmed" ? "Slot confirmed!" : "Added to waitlist");
      await loadJob();
      await loadUserState(job?.posted_by);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => {
    if (!user || !job || applied || isOwner) return;
    try {
      const raw = localStorage.getItem("em_pending_apply");
      if (!raw) return;
      const pending = JSON.parse(raw);
      if (pending.jobId === id && Date.now() - pending.ts < 30 * 60 * 1000) {
        setShowApplyConfirm(true);
      }
    } catch {} finally {
      localStorage.removeItem("em_pending_apply");
    }
  }, [user, job, id, applied, isOwner]);
    


  const submitRating = async () => {
    if (!ratingStars) { toast.error("Please select a star rating"); return; }
    setSubmittingRating(true);
    try {
      await api.post("/ratings", { rated_user_id: job.posted_by, job_id: id, stars: ratingStars, comment: ratingComment });
      toast.success("Review submitted!");
      setAlreadyRated(true);
      await loadJob();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSubmittingRating(false); }
  };

  if (!job) return <div className="p-12 text-center text-muted-foreground">Loading…</div>;

  const canReview = applied && appStatus === "confirmed" && !alreadyRated;

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-8" data-testid="job-detail-page">
      <span className="label-eyebrow text-muted-foreground">
        {job.opportunity_type === "event_staffing" ? "Event Staffing" : "Freelance"}
      </span>
      <h1 className="mt-3 font-display text-3xl sm:text-5xl tracking-tighter">{job.title}</h1>
      <div className="mt-4 flex flex-wrap gap-3 text-sm text-muted-foreground">
        <span className="font-mono text-xs px-2 py-1 bg-secondary text-secondary-foreground rounded-sm">{job.specialization}</span>
        {job.location && <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />{job.location}</span>}
        {job.event_date && <span className="inline-flex items-center gap-1"><Calendar className="w-4 h-4" />{new Date(job.event_date).toLocaleString()}</span>}
        <span className="inline-flex items-center gap-1"><IndianRupee className="w-4 h-4" />{job.payment}</span>
      </div>

      {/* ── DESKTOP LAYOUT (unchanged) ── */}
      <div className="mt-8 hidden md:grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          <div>
            <h2 className="font-display text-2xl mb-3">About this gig</h2>
            <p className="whitespace-pre-line text-muted-foreground leading-relaxed">{job.description}</p>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Stat label="Slots filled" value={job.confirmed_count} />
            <Stat label="Remaining" value={job.remaining_slots} />
            <Stat label="Waitlist" value={job.waitlist_count} />
          </div>

          {!isOwner && (
            <div className="card-flat">
              <p className="label-eyebrow text-muted-foreground">Posted by</p>
              <Link to={`/profile/${job.posted_by_username}`} className="mt-2 font-display text-xl underline underline-offset-4 inline-flex items-center gap-1.5">
                {job.posted_by_name}
                {job.poster_verified && <VerifiedBadge size={16} />}
              </Link>
              {job.poster_rating && (
                <div className="mt-1">
                  <StarRating rating={job.poster_rating} count={job.poster_rating_count} size="sm" />
                </div>
              )}
            </div>
          )}

          {canReview && (
            <div className="card-flat">
              <h3 className="font-display text-lg mb-3">Rate the organizer</h3>
              <p className="text-sm text-muted-foreground mb-3">How was your experience with {job.posted_by_name}?</p>
              <div className="flex gap-2 mb-3">
                <StarRating rating={ratingStars} size="lg" interactive onRate={setRatingStars} />
              </div>
              <textarea rows={3} className="input-base" placeholder="Write a review (optional)…" value={ratingComment} onChange={e => setRatingComment(e.target.value)} />
              <button onClick={submitRating} disabled={submittingRating || !ratingStars} className="btn-primary mt-3">
                {submittingRating ? "Submitting…" : "Submit review"}
              </button>
            </div>
          )}

          {alreadyRated && (
            <div className="card-flat bg-emerald-50 border-emerald-200">
              <p className="text-sm text-emerald-700">✓ You've already reviewed this organizer.</p>
            </div>
          )}

          {ratings.length > 0 && (
            <div>
              <h3 className="font-display text-xl mb-3">Reviews ({ratings.length})</h3>
              <div className="space-y-3">
                {ratings.slice(0, 5).map(r => (
                  <div key={r.id} className="card-flat">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">{r.rater_name}</span>
                      <StarRating rating={r.stars} size="sm" />
                    </div>
                    {r.comment && <p className="text-sm text-slate-600">{r.comment}</p>}
                    <p className="text-xs text-muted-foreground mt-1">{new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="space-y-4">
          <div className="card-flat">
            {isOwner ? (
              <div className="space-y-3">
                <span className="text-xs px-2 py-1 rounded-sm bg-brand/10 text-brand font-medium">
                  Your job post
                </span>
                <Link to={`/manage/${id}?from=job`} className="btn-primary w-full flex items-center justify-center gap-2 mt-2">
                  <Settings className="w-4 h-4" /> Manage this job
                </Link>
                <p className="text-xs text-muted-foreground text-center">
                  View applicants, mark attendance, update slots
                </p>
              </div>
            ) : (
              <div>
                {job.fine_policy && (job.fine_policy.categories?.length > 0 || job.fine_policy.brackets?.length > 0) && (
                  <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <p className="text-xs font-semibold text-amber-700 mb-1">⚠ This job has rating fines</p>
                    {job.fine_policy.notes && <p className="text-xs text-amber-700 mb-2">{job.fine_policy.notes}</p>}
                    {job.fine_policy.categories?.length > 0 && (
                      <p className="text-[11px] text-amber-600">
                        Categories: {job.fine_policy.categories.map(c => `${c.name} (₹${c.rate_per_star}/star)`).join(", ")}
                      </p>
                    )}
                    {job.fine_policy.brackets?.length > 0 && (
                      <p className="text-[11px] text-amber-600 mt-1">
                        Delay fines: {job.fine_policy.brackets.map(b => `${b.min_minutes}-${b.max_minutes ?? "+"} min: ₹${b.fine}`).join(", ")}
                      </p>
                    )}
                  </div>
                )}
                {!applied ? (
                  <button onClick={apply} className="btn-primary w-full" data-testid="apply-btn">
                    <Clock className="w-4 h-4" /> {job.remaining_slots > 0 ? "Apply now" : "Join waitlist"}
                  </button>
                ) : (
                 <div className="space-y-3">
                    <p className="text-sm">Status: <strong className="capitalize">{appStatus}</strong></p>
                    {myApp?.attendance === "present" && (
                      <p className={`text-sm font-medium ${myApp.payment_paid ? "text-emerald-600" : "text-amber-600"}`}>
                        Payment: {myApp.payment_paid ? "✓ Paid" : "Pending"}
                      </p>
                    )}
                    <p className="btn-outline w-full text-center cursor-default" data-testid="cancel-app-btn">To cancel application, contact manager</p>
                  </div>
                )}
                {job.opportunity_type === "freelance" && user && (
                  <button onClick={() => nav(`/messages/${job.posted_by}`)} className="btn-tint w-full mt-2" data-testid="chat-btn">
                    💬 Chat with poster
                  </button>
                )}
              </div>
            )}
            <p className="mt-3 text-xs text-muted-foreground">
              <Users className="inline w-3 h-3" /> {job.confirmed_count}/{job.total_slots} confirmed
            </p>
          </div>

          <div className="card-flat">
            <p className="label-eyebrow text-muted-foreground mb-3">Share</p>
           <ShareButtons
              url={window.location.href}
              title={job.title}
              text={buildShareText(job)}
             previewUrl={`https://earnmate-og.earnmatein.workers.dev/share/${job.id}`}
            />
          </div>
        </aside>
      </div>

      {/* ── MOBILE LAYOUT: About → Posted by → Apply now → Share → Rate/Reviews ── */}
      <div className="mt-8 md:hidden space-y-6">
        <div>
          <h2 className="font-display text-2xl mb-3">About this gig</h2>
          <p className="whitespace-pre-line text-muted-foreground leading-relaxed">{job.description}</p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Stat label="Slots filled" value={job.confirmed_count} />
          <Stat label="Remaining" value={job.remaining_slots} />
          <Stat label="Waitlist" value={job.waitlist_count} />
        </div>

        {!isOwner && (
          <div className="card-flat">
            <p className="label-eyebrow text-muted-foreground">Posted by</p>
            <Link to={`/profile/${job.posted_by_username}`} className="mt-2 font-display text-xl underline underline-offset-4 inline-flex items-center gap-1.5">
              {job.posted_by_name}
              {job.poster_verified && <VerifiedBadge size={16} />}
            </Link>
            {job.poster_rating && (
              <div className="mt-1">
                <StarRating rating={job.poster_rating} count={job.poster_rating_count} size="sm" />
              </div>
            )}
          </div>
        )}

        <div className="card-flat">
          {isOwner ? (
            <div className="space-y-3">
              <span className="text-xs px-2 py-1 rounded-sm bg-brand/10 text-brand font-medium">
                Your job post
              </span>
              <Link to={`/manage/${id}?from=job`} className="btn-primary w-full flex items-center justify-center gap-2 mt-2">
                <Settings className="w-4 h-4" /> Manage this job
              </Link>
              <p className="text-xs text-muted-foreground text-center">
                View applicants, mark attendance, update slots
              </p>
            </div>
          ) : (
            <div>
              {job.fine_policy && (job.fine_policy.categories?.length > 0 || job.fine_policy.brackets?.length > 0) && (
                <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <p className="text-xs font-semibold text-amber-700 mb-1">⚠ This job has rating fines</p>
                  {job.fine_policy.notes && <p className="text-xs text-amber-700 mb-2">{job.fine_policy.notes}</p>}
                  {job.fine_policy.categories?.length > 0 && (
                    <p className="text-[11px] text-amber-600">
                      Categories: {job.fine_policy.categories.map(c => `${c.name} (₹${c.rate_per_star}/star)`).join(", ")}
                    </p>
                  )}
                  {job.fine_policy.brackets?.length > 0 && (
                    <p className="text-[11px] text-amber-600 mt-1">
                      Delay fines: {job.fine_policy.brackets.map(b => `${b.min_minutes}-${b.max_minutes ?? "+"} min: ₹${b.fine}`).join(", ")}
                    </p>
                  )}
                </div>
              )}
              {!applied ? (
                <button onClick={apply} className="btn-primary w-full" data-testid="apply-btn-mobile">
                  <Clock className="w-4 h-4" /> {job.remaining_slots > 0 ? "Apply now" : "Join waitlist"}
                </button>
              ) : (
               <div className="space-y-3">
                  <p className="text-sm">Status: <strong className="capitalize">{appStatus}</strong></p>
                  {myApp?.attendance === "present" && (
                    <p className={`text-sm font-medium ${myApp.payment_paid ? "text-emerald-600" : "text-amber-600"}`}>
                      Payment: {myApp.payment_paid ? "✓ Paid" : "Pending"}
                    </p>
                  )}
                  <p className="btn-outline w-full text-center cursor-default" data-testid="cancel-app-btn-mobile">To cancel application, contact manager</p>
                </div>
              )}
              {job.opportunity_type === "freelance" && user && (
                <button onClick={() => nav(`/messages/${job.posted_by}`)} className="btn-tint w-full mt-2" data-testid="chat-btn-mobile">
                  💬 Chat with poster
                </button>
              )}
            </div>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            <Users className="inline w-3 h-3" /> {job.confirmed_count}/{job.total_slots} confirmed
          </p>
        </div>

        <div className="card-flat">
          <p className="label-eyebrow text-muted-foreground mb-3">Share</p>
         <ShareButtons
            url={window.location.href}
            title={job.title}
            text={buildShareText(job)}
           previewUrl={`https://earnmate-og.earnmatein.workers.dev/share/${job.id}`}
          />
        </div>

        {canReview && (
          <div className="card-flat">
            <h3 className="font-display text-lg mb-3">Rate the organizer</h3>
            <p className="text-sm text-muted-foreground mb-3">How was your experience with {job.posted_by_name}?</p>
            <div className="flex gap-2 mb-3">
              <StarRating rating={ratingStars} size="lg" interactive onRate={setRatingStars} />
            </div>
            <textarea rows={3} className="input-base" placeholder="Write a review (optional)…" value={ratingComment} onChange={e => setRatingComment(e.target.value)} />
            <button onClick={submitRating} disabled={submittingRating || !ratingStars} className="btn-primary mt-3">
              {submittingRating ? "Submitting…" : "Submit review"}
            </button>
          </div>
        )}

        {alreadyRated && (
          <div className="card-flat bg-emerald-50 border-emerald-200">
            <p className="text-sm text-emerald-700">✓ You've already reviewed this organizer.</p>
          </div>
        )}

        {ratings.length > 0 && (
          <div>
            <h3 className="font-display text-xl mb-3">Reviews ({ratings.length})</h3>
            <div className="space-y-3">
              {ratings.slice(0, 5).map(r => (
                <div key={r.id} className="card-flat">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm">{r.rater_name}</span>
                    <StarRating rating={r.stars} size="sm" />
                  </div>
                  {r.comment && <p className="text-sm text-slate-600">{r.comment}</p>}
                  <p className="text-xs text-muted-foreground mt-1">{new Date(r.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {job.event_id && (
        <div className="mt-6 card-flat flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-foreground">This job is part of an event</p>
            <p className="text-xs text-muted-foreground">See all roles available under this event</p>
          </div>
          <Link to={`/events/${job.event_id}`} className="btn-tint text-sm whitespace-nowrap">See all jobs in event →</Link>
        </div>
      )}

     {!isOwner && Array.isArray(job.more_from_manager) && job.more_from_manager.length > 0 && (
        <section className="mt-10">
          <h3 className="font-display text-xl mb-4">More from @{job.posted_by_username}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {job.more_from_manager.map(j => <JobCard key={j.id} job={j} />)}
          </div>
        </section>
      )}

      {showApplyConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={() => setShowApplyConfirm(false)}>
          <div className="card-flat max-w-sm w-full bg-white" onClick={e => e.stopPropagation()}>
            <h3 className="font-display text-xl mb-2">Confirm application</h3>
            <p className="text-sm text-slate-600 mb-4">
              You're applying for <strong>{job.title}</strong>. Please apply only if you are willing to actually do this job. Once submitted, you won't be able to cancel this on your own — you'll need to contact the manager to withdraw.
            </p>

            {job.travel_expense_enabled && user?.lat == null && (
              <div className="mb-4">
                <label className="label-eyebrow text-muted-foreground">Home location <span className="text-xs normal-case text-muted-foreground">(optional)</span></label>
                <div className="mt-2">
                  <LocationAutocomplete
                    value={applyHomeLocation}
                    onChange={(val) => { setApplyHomeLocation(val); setApplyHomeLat(null); setApplyHomeLng(null); }}
                    onSelect={({ display, lat, lng }) => { setApplyHomeLocation(display); setApplyHomeLat(lat); setApplyHomeLng(lng); }}
                    placeholder="Search your home area…"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">This job offers a travel allowance — add your home location to be eligible.</p>
              </div>
            )}

            <div className="flex gap-2">
              <button onClick={() => setShowApplyConfirm(false)} className="btn-outline w-full">Go back</button>
              <button onClick={confirmApply} className="btn-primary w-full" data-testid="confirm-apply-btn">Yes, apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function buildShareText(job) {
  const lines = [job.title];

  if (job.posted_by_name) lines.push(`Posted by ${job.posted_by_name}`);

  const venueLine = [];
  if (job.location) venueLine.push(job.location);
  if (job.event_date) {
    const d = new Date(job.event_date);
    const datePart = d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    const timePart = d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
    venueLine.push(`${datePart}, ${timePart}`);
  }
  if (venueLine.length) lines.push(venueLine.join(" | "));

  if (job.description) lines.push(`\n${job.description}`);
  if (job.payment) lines.push(`\nPayment: ₹${job.payment}`);

  return lines.join("\n");
}
function Stat({ label, value }) {
  return (
    <div className="border border-slate-200 p-4 rounded-sm">
      <p className="font-display text-3xl font-black tabular-nums">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </div>
  );
}
